// Management › Task overwatch (11a §12) + Sales SLA (moved from My Dashboard). Admin + Management only.
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Navigate } from "react-router-dom";
import axios from "axios";
import { Eye, Gauge, Activity } from "lucide-react";
import Layout from "../../components/Layout";
import { useAuth } from "../../context/AuthContext";
import "../workspace/workspace.css";
import { workspaceApi, withMe, isMgmt, dueInfo, TASK_CHANGED } from "../workspace/_shared";
import TaskTable from "../workspace/panels/TaskTable";
import TaskDrawer from "../workspace/panels/TaskDrawer";
import { ManagerBar } from "../dashboard/MyDashboard";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STALE_DAYS = 7; // ≈5 working days

export function Tabs() {
  const cls = ({ isActive }) => `inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-mono uppercase tracking-widest border-b-2 -mb-px ${isActive ? "border-amber-400 text-amber-200" : "border-transparent text-slate-400 hover:text-white"}`;
  return (
    <nav className="flex gap-1 mb-5 border-b border-white/[0.06]" data-testid="mgmt-tabs">
      <NavLink to="/management/tasks" className={cls} data-testid="mgmt-tab-tasks"><Eye className="w-3.5 h-3.5" />Task overwatch</NavLink>
      <NavLink to="/management/productivity" className={cls} data-testid="mgmt-tab-productivity"><Activity className="w-3.5 h-3.5" />Staff productivity</NavLink>
      <NavLink to="/management/sales-sla" className={cls} data-testid="mgmt-tab-sla"><Gauge className="w-3.5 h-3.5" />Sales SLA</NavLink>
    </nav>
  );
}

function Workload({ tasks, userMap, onPick, picked }) {
  const by = {};
  tasks.forEach((t) => {
    const k = t.assignee_id || (t.assign_mode === "each" ? "_each" : t.team_id ? "_pool" : "_none");
    const b = (by[k] = by[k] || { open: 0, overdue: 0, today: 0, waiting: 0, done7: 0, missed: 0 });
    const closed = ["done", "cancelled"].includes(t.status);
    if (t.status === "done" && t.completed_at && Date.now() - new Date(t.completed_at) < 7 * 86_400_000) b.done7 += 1;
    if (closed) return;
    b.open += 1;
    const d = dueInfo(t);
    if (d.tone === "red") b.overdue += 1;
    if (d.days === 0) b.today += 1;
    if (["waiting", "blocked"].includes(t.status)) b.waiting += 1;
    if (t.ack_required && !(t.acks || []).some((a) => Date.now() - new Date(a.at) < 7 * 86_400_000)) b.missed += 1;
  });
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-2" data-testid="mgmt-workload">
      {Object.entries(by).sort((a, b) => b[1].overdue - a[1].overdue).map(([k, b]) => (
        <button key={k} onClick={() => onPick(picked === k ? "" : k)} data-testid={`mgmt-person-${k}`}
          className={`text-left rounded-xl border p-3 transition-colors ${picked === k ? "border-teal-400/60 bg-teal-500/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
          <div className="text-[13px] font-semibold text-white">{{ _pool: "Team pool", _none: "Unassigned", _each: "Everyone (each member)" }[k] || userMap[k]?.name || "Someone"}</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1.5 text-[11px] font-mono text-slate-400 whitespace-nowrap">
            <span>{b.open} open</span><span className={b.overdue ? "text-rose-300" : ""}>{b.overdue} late</span><span>{b.today} today</span>
            <span className={b.waiting ? "text-amber-300" : ""}>{b.waiting} waiting</span><span className="text-emerald-300">{b.done7} done 7d</span><span className={b.missed ? "text-amber-300" : ""}>{b.missed} no check-in</span>
          </div>
        </button>
      ))}
    </div>
  );
}

export function ManagementTasks() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [person, setPerson] = useState("");
  const [attention, setAttention] = useState(false);
  const [groupBy, setGroupBy] = useState("owner");
  const [openTask, setOpenTask] = useState(null);
  const load = useCallback(async () => {
    const [c, u, t, ts] = await Promise.all([workspaceApi.getConfig(), workspaceApi.listUsers(), workspaceApi.listTeams("all"), workspaceApi.listTasks({ scope: "all", limit: 2000 })]);
    setCfg(c); setUsers(u || []); setTeams(t.items || []); setTasks(ts.items || []);
  }, []);
  useEffect(() => { load(); window.addEventListener(TASK_CHANGED, load); return () => window.removeEventListener(TASK_CHANGED, load); }, [load]);
  const userMap = useMemo(() => Object.fromEntries(withMe(users, user).map((u) => [u.id, u])), [users, user]);
  const teamMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const shown = tasks.filter((t) => !["done", "cancelled"].includes(t.status)
    && (!person || (person === "_pool" ? (!t.assignee_id && t.team_id && t.assign_mode !== "each") : person === "_each" ? t.assign_mode === "each" : person === "_none" ? (!t.assignee_id && !t.team_id) : t.assignee_id === person))
    && (!attention || dueInfo(t).tone === "red" || ["blocked", "waiting"].includes(t.status) || (Date.now() - new Date(t.last_activity_at || t.updated_at)) > STALE_DAYS * 86_400_000));
  if (!isMgmt(user)) return <Navigate to="/" replace />;
  return (
    <Layout>
      <h1 className="text-xl font-bold text-white mb-3">Management</h1>
      <Tabs />
      <div className="space-y-4" data-testid="mgmt-task-overwatch">
        <Workload tasks={tasks} userMap={userMap} picked={person} onPick={setPerson} />
        <div className="flex items-center gap-2">
          <button onClick={() => setAttention((a) => !a)} className={`ws-chip ${attention ? "on" : ""}`} data-testid="mgmt-attention">Needs attention (overdue · blocked · waiting · stale)</button>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="ws-input max-w-[160px] ml-auto" data-testid="mgmt-groupby">
            {[["owner", "Group: owner"], ["team", "Group: team"], ["status", "Group: status"], ["none", "No grouping"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <TaskTable tasks={shown} groupBy={groupBy} statuses={cfg?.statuses || []} userMap={userMap} teamMap={teamMap} onOpen={(t) => setOpenTask(t.id)} onChanged={load} emptyText="No open tasks match." />
      </div>
      <TaskDrawer open={!!openTask} taskId={openTask} users={users} teams={teams} onClose={() => setOpenTask(null)} onSaved={load} />
    </Layout>
  );
}

export function ManagementSalesSla() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${API}/me/cockpit`, { withCredentials: true }).then((r) => setRows(r.data.manager_sla || [])).catch(() => setRows([])); }, []);
  if (!isMgmt(user)) return <Navigate to="/" replace />;
  return (
    <Layout>
      <h1 className="text-xl font-bold text-white mb-3">Management</h1>
      <Tabs />
      <div data-testid="mgmt-sales-sla">
        {rows === null ? <div className="text-slate-500 py-10 text-center">Loading…</div>
          : <ManagerBar open onToggle={() => {}} rows={rows} onView={(am) => window.location.assign(`/my-dashboard?as=${encodeURIComponent(am)}`)} />}
      </div>
    </Layout>
  );
}
